import { useCallback, useState } from "react";

export interface FetchPageResult {
  items: { id: string; name: string }[];
  totalElements: number;
  totalPages: number;
}

export type FetchPageFn = (page: number, size: number) => Promise<FetchPageResult>;

interface UseFetchAllOrgIdsResult {
  fetchAll: () => Promise<{ id: string; name: string }[]>;
  isLoading: boolean;
  progress: { loaded: number; total: number } | null;
  error: string | null;
}

const PAGE_SIZE = 100;
const MAX_PAGES = 1000;

/**
 * Iterates every page of a paginated org-list endpoint and returns the
 * deduplicated org list. Used by "select all matching filter" actions so a
 * report can be requested for every org in the current view.
 *
 * The fetchPage callback is provided by each surface and must apply the same
 * filters the user currently sees.
 */
export const useFetchAllOrgIds = (fetchPage: FetchPageFn): UseFetchAllOrgIdsResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async (): Promise<{ id: string; name: string }[]> => {
    const all = new Map<string, string>();
    let page = 0;
    let total = 0;
    setIsLoading(true);
    setError(null);
    setProgress({ loaded: 0, total: 0 });
    try {
      let hasMore = true;
      while (hasMore) {
        const result = await fetchPage(page, PAGE_SIZE);
        result.items.forEach((item) => {
          if (item.id) all.set(item.id, item.name);
        });
        total = result.totalElements || all.size;
        setProgress({ loaded: all.size, total });
        const isFullPage = result.items.length === PAGE_SIZE;
        const withinTotalPages =
          result.totalPages > 0 ? page + 1 < result.totalPages : true;
        hasMore = isFullPage && withinTotalPages && page < MAX_PAGES;
        page += 1;
      }
      return Array.from(all.entries()).map(([id, name]) => ({ id, name }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error loading organizations");
      return [];
    } finally {
      setIsLoading(false);
      setProgress(null);
    }
  }, [fetchPage]);

  return { fetchAll, isLoading, progress, error };
};
